// TUT-530 — o envio ao advogado: o lado do escritório (o Agendamento envia e
// lê o que espera o advogado) e o lado do portal (o advogado lê, manda o
// comprovante e responde ao "Outros").
import { demoApi } from "../demo-data";
import {
  demoCancelarEnvio,
  demoDestinatarios,
  demoEnvios,
  demoGravarEnvio,
  demoPrepararEnvio,
  demoPortalEnvioAccessLink,
  demoPortalEnviarComprovanteDoEnvio,
  demoPortalEnviarComprovanteDoEscritorio,
  demoPortalEnvios,
  demoPortalResponderEnvio,
} from "../demo/envios";
import type {
  DestinatariosDoEnvio,
  EnvioAoAdvogado,
  EnviarAoAdvogadoInput,
  EnviosAoAdvogadoQuery,
  EnviosAoAdvogadoResponse,
  EnviosDoPortalResponse,
  EnvioDoPortal,
  ResponderEnvioInput,
} from "../types";
import { incluirDocumentoDoPortalNoProcesso } from "./ponte-do-portal";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

/** No demo, a recusa do servidor vira a promessa rejeitada com a mesma frase. */
function noDemo<T>(trabalho: () => T): Promise<T> {
  try {
    return Promise.resolve(trabalho());
  } catch (erro) {
    return Promise.reject(erro instanceof Error ? erro : new Error(String(erro)));
  }
}

const id = encodeURIComponent;

/**
 * O envio no modo demonstração, em dois passos (ver `demoPrepararEnvio`): confere
 * e monta, conclui o item na MESMA operação (cliente sem a marca, D19) e só então
 * grava o envio — a transição recusada não deixa envio pela metade. Mora aqui
 * porque a transição é do `demoApi`, que os módulos de `lib/demo` não importam.
 */
export async function enviarAoAdvogadoNoDemo(input: EnviarAoAdvogadoInput): Promise<EnvioAoAdvogado> {
  const { envio, concluir } = demoPrepararEnvio(input);
  if (concluir?.kind === "LEGAL_BRIEF") await demoApi.transitionLegalBrief(concluir.id, { to: "DELIVERED" });
  else if (concluir) await demoApi.transitionExpertReport(concluir.id, { to: "filed" });
  return demoGravarEnvio(envio);
}

/**
 * O comprovante da advogada no modo demonstração. O envio que o ESCRITÓRIO
 * fez (a ponte, TUT-531) fecha como no servidor (`EnviosService.enviarComprovante`):
 * o item sai de "Aguardando protocolo" — a peça vira entregue, o laudo
 * protocolado — e o comprovante cai nos documentos do processo, com a advogada
 * como quem incluiu. Os envios semeados do portal seguem pelo caminho antigo.
 */
export async function enviarComprovanteNoDemo(envioId: string, documentId: string): Promise<EnvioDoPortal> {
  const feito = demoPortalEnviarComprovanteDoEscritorio(envioId, documentId);
  if (!feito) return demoPortalEnviarComprovanteDoEnvio(envioId, documentId);
  const { doEscritorio, comprovante, envio } = feito;
  if (doEscritorio.legalBriefId) await demoApi.transitionLegalBrief(doEscritorio.legalBriefId, { to: "DELIVERED" });
  else if (doEscritorio.expertReportId) await demoApi.transitionExpertReport(doEscritorio.expertReportId, { to: "filed" });
  await incluirDocumentoDoPortalNoProcesso(doEscritorio.processId, comprovante);
  return envio;
}

export const apiDosEnvios = {
  /* ------------------------------ escritório ------------------------------ */
  enviosAoAdvogado(query: EnviosAoAdvogadoQuery = {}): Promise<EnviosAoAdvogadoResponse> {
    if (useDemoApi) return noDemo(() => demoEnvios(query));
    const busca = new URLSearchParams();
    if (query.status) busca.set("status", query.status);
    if (query.kind) busca.set("kind", query.kind);
    if (query.processId) busca.set("processId", query.processId);
    if (query.waiting) busca.set("waiting", "true");
    if (query.pageSize) busca.set("pageSize", String(query.pageSize));
    const sufixo = busca.size > 0 ? `?${busca.toString()}` : "";
    return request(`/envios${sufixo}`);
  },
  /** Quem receberia o envio deste processo: o diálogo mostra o nome ANTES de enviar. */
  destinatariosDoEnvio(processId: string): Promise<DestinatariosDoEnvio> {
    if (useDemoApi) return noDemo(() => demoDestinatarios(processId));
    return request(`/envios/destinatarios?processId=${id(processId)}`);
  },
  enviarAoAdvogado(input: EnviarAoAdvogadoInput): Promise<EnvioAoAdvogado> {
    if (useDemoApi) return enviarAoAdvogadoNoDemo(input);
    return request("/envios", { method: "POST", body: JSON.stringify(input) });
  },
  /** TUT-554 (T7-M1) — "Cancelar envio", enquanto o advogado não respondeu. */
  cancelarEnvioAoAdvogado(envioId: string): Promise<EnvioAoAdvogado> {
    if (useDemoApi) return noDemo(() => demoCancelarEnvio(envioId));
    return request(`/envios/${id(envioId)}/cancelar`, { method: "POST" });
  },

  /* -------------------------------- portal -------------------------------- */
  portalEnvios(): Promise<EnviosDoPortalResponse> {
    if (useDemoApi) return noDemo(() => demoPortalEnvios());
    return request("/portal/envios");
  },
  portalEnvioAccessLink(envioId: string, mode: "download" | "inline"): Promise<{ url: string; expiresInSeconds: number }> {
    if (useDemoApi) return noDemo(() => demoPortalEnvioAccessLink());
    const sufixo = mode === "inline" ? "?modo=inline" : "";
    return request(`/portal/envios/${id(envioId)}/download${sufixo}`);
  },
  portalEnviarComprovanteDoEnvio(envioId: string, documentId: string): Promise<EnvioDoPortal> {
    if (useDemoApi) return enviarComprovanteNoDemo(envioId, documentId);
    return request(`/portal/envios/${id(envioId)}/comprovante`, {
      method: "POST",
      body: JSON.stringify({ documentId }),
    });
  },
  portalResponderEnvio(envioId: string, input: ResponderEnvioInput): Promise<EnvioDoPortal> {
    if (useDemoApi) return noDemo(() => demoPortalResponderEnvio(envioId, input));
    return request(`/portal/envios/${id(envioId)}/resposta`, { method: "POST", body: JSON.stringify(input) });
  },
};
